import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import User from "../utils/user";
import { toast, ToastContainer } from "react-toastify"; // Assuming you're using react-toastify for toasts
import { useNavigate } from "react-router-dom";
import { handleLogoutUser } from "../utils/utility";
import { AppDispatch } from "../app/store";

interface UserState {
  user: User | null;
  loading: boolean;
  error: string | null;
  totalTransactions: number;
  history: History[] | null;
  isHistoryLoading: boolean;
  historyError: string | null;
  totalHistory: number;
}

const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
  totalTransactions: 0,
  history: null,
  isHistoryLoading: true,
  historyError: null,
  totalHistory: 0,
};

interface ErrorDetail {
  loc: [string, number];
  msg: string;
  type: string;
}

interface ErrorResponse {
  detail: ErrorDetail[];
}

export interface Transaction {
  id: string;
  user_id: string;
  type: string;
  description: string;
  debit: number;
  credit: number;
  balance_before: number;
  balance_after: number;
  created_at: string;
  box_id: string;
  box_title: string;
  payment_type: string;
}

export interface BoxDetails {
  id: string;
  name: string;
  description: string;
  picture: string;
  tags: string[];
  level: number;
  value: number;
  final_value: number;
  items_value: number | null;
  is_drawable: boolean;
  stock_count: number;
  is_featured: boolean;
  retired: boolean;
  items: any[]; // Define item type if known
  surcharge: number;
  opening_video: string | null;
  is_vip: boolean;
  vip_level: number;
}

export interface ItemDetails {
  id: string;
  name: string;
  description: string;
  picture: string;
  tags: string[];
  price: number;
  stock_count: number;
  retired: boolean;
}

export interface History {
  id: string;
  box_id: string;
  item_id: string;
  user_id: string;
  timestamp: string;
  box_opened?: BoxDetails;
  item_won: ItemDetails;
  accepted: boolean;
  shipped: boolean;
}

// Async thunk for fetching user transactions with pagination
export const getUserHistory = createAsyncThunk<
  { history: History[]; total: number }, // Return type
  { offset: number; limit: number }, // Parameters
  {
    dispatch: AppDispatch; // Dispatch type
    rejectValue: string; // Reject value type (string in this case)
  }
>(
  "user/getUserHistory",
  async ({ offset, limit }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`boxes/history`, {
        params: {
          offset,
          limit,
        },
      });

      return { history: response.data.list, total: response.data.total }; // Ensure total count is returned by the API
    } catch (error: any) {
      // Check for 401 Unauthorized status
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate()); // Dispatch the logout action
        return rejectWithValue("Unauthorized access - logging out");
      }

      // Handle other errors and reject with a message
      return rejectWithValue("Failed to fetch transactions");
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserHistory.pending, (state) => {
        state.isHistoryLoading = true;
        state.historyError = null;
      })
      .addCase(
        getUserHistory.fulfilled,
        (
          state,
          action: PayloadAction<{ history: History[]; total: number }>
        ) => {
          state.isHistoryLoading = false;
          state.history = action.payload.history;
          state.totalHistory = action.payload.total; // Update total transactions
        }
      )
      .addCase(getUserHistory.rejected, (state, action) => {
        state.isHistoryLoading = false;
        state.historyError = action.payload as string;
      });
  },
});

export const {} = userSlice.actions;
export default userSlice.reducer;
